import React, { useState, useEffect } from 'react';
import { Menu, X, Sparkles, ChevronRight } from 'lucide-react';

interface HeaderProps {
    navigateTo: (page: string) => void;
    onLoginClick: () => void;
    currentPage: string;
}

export const Header = ({ navigateTo, onLoginClick, currentPage }: HeaderProps) => {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 20);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const isLinkActive = (page: string) => {
        if (page === 'tenant' && currentPage === 'tenant-landing') return true;
        if (page === 'landlord' && currentPage === 'host-landing') return true;
        if (page === 'roommate' && currentPage === 'roommate-landing') return true;
        return false;
    };

    const handleMobileLinkClick = (page: string) => {
        setMobileMenuOpen(false);
        navigateTo(page);
    };

    return (
        <>
            <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 px-4 sm:px-6 lg:px-8 bg-transparent ${isScrolled ? 'pt-3 pb-3' : 'pt-4 pb-2'}`}>
                <div className="max-w-4xl mx-auto bg-white/80 backdrop-blur-md rounded-full shadow-[0_12px_40px_-12px_rgba(0,0,0,0.08)] border border-gray-100/80 px-6 py-2 md:py-2.5 flex items-center justify-between">
                    {/* Logo */}
                    <div className="flex items-center">
                        <button 
                            onClick={() => navigateTo('tenant-landing')}
                            className="flex items-center text-xl lg:text-2xl font-black tracking-tighter text-black transition-colors duration-300"
                        >
                            <span>coleese</span>
                        </button>
                    </div>

                    {/* Desktop Navigation Link Menu */}
                    <nav className="hidden md:flex items-center gap-8">
                        <button 
                            onClick={() => navigateTo('tenant-landing')}
                            className={`text-sm font-semibold tracking-tight transition-colors ${isLinkActive('tenant') ? 'text-black font-extrabold' : 'text-gray-400 hover:text-black'}`}
                        >
                            Tenant
                        </button>
                        <button 
                            onClick={() => navigateTo('host-landing')}
                            className={`text-sm font-semibold tracking-tight transition-colors ${isLinkActive('landlord') ? 'text-black font-extrabold' : 'text-gray-400 hover:text-black'}`}
                        >
                            Landlord
                        </button>
                        <button 
                            onClick={() => navigateTo('roommate-landing')}
                            className={`text-sm font-semibold tracking-tight transition-colors ${isLinkActive('roommate') ? 'text-black font-extrabold' : 'text-gray-400 hover:text-black'}`}
                        >
                            Roommate
                        </button>
                    </nav>

                    {/* Desktop CTA Button */}
                    <div className="hidden md:block">
                        <button 
                            onClick={onLoginClick} 
                            className="bg-black text-white font-extrabold py-2 px-6 rounded-full text-xs lg:text-sm hover:bg-gray-800 transition-all active:scale-95 shadow-md shadow-black/5"
                        >
                            Get access
                        </button>
                    </div>

                    {/* Mobile Hamburger / X Button */}
                    <div className="md:hidden">
                        <button 
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            className="p-2 text-gray-700 hover:text-black focus:outline-none"
                            aria-label="Toggle Menu"
                        >
                            {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
                        </button>
                    </div>
                </div>
            </header>

            {/* Mobile Drawer Overlay */}
            {mobileMenuOpen && (
                <div className="fixed inset-0 z-40 bg-white pt-24 px-6 flex flex-col md:hidden animate-fade-in">
                    <div className="flex flex-col gap-6 text-center mt-8">
                        <button 
                            onClick={() => handleMobileLinkClick('tenant-landing')}
                            className={`text-md font-black py-3 border-b border-gray-50 flex items-center justify-between px-4 ${isLinkActive('tenant') ? 'text-black' : 'text-gray-400'}`}
                        >
                            <span>Tenants</span>
                            <ChevronRight size={18} className="text-gray-300" />
                        </button>
                        <button 
                            onClick={() => handleMobileLinkClick('host-landing')}
                            className={`text-md font-black py-3 border-b border-gray-50 flex items-center justify-between px-4 ${isLinkActive('landlord') ? 'text-black' : 'text-gray-400'}`}
                        >
                            <span>Landlords</span>
                            <ChevronRight size={18} className="text-gray-300" />
                        </button>
                        <button 
                            onClick={() => handleMobileLinkClick('roommate-landing')}
                            className={`text-md font-black py-3 border-b border-gray-50 flex items-center justify-between px-4 ${isLinkActive('roommate') ? 'text-black' : 'text-gray-400'}`}
                        >
                            <span>Roommates</span>
                            <ChevronRight size={18} className="text-gray-300" />
                        </button>

                        <div className="mt-12 px-4">
                            <button 
                                onClick={() => { setMobileMenuOpen(false); onLoginClick(); }}
                                className="w-full bg-black text-white font-extrabold py-4 px-6 rounded-2xl text-md hover:bg-gray-800 transition-all shadow-xl text-sm"
                            >
                                Get access
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};
